import React, { useEffect, useState } from 'react'
import Container from '../../../v2/Container'
import Image from '../../../Image'
import landingPageImg from '../../../../assets/purchase-yearbooks-landing-page.jpg'
import useQueryString from '../../../../hooks/use-query-string'
import { Package, Price, Student } from '../../../../models'
import Fetching from './Fetching'
import ErrorState from './ErrorState'
import YearbookForm from './YearbookForm'
import useLocalStorage from '../../../../hooks/useLocalStorage';
import Typography from '../../../Typography';
import StudentPromoImage from './StudentPromoImage';
import usePastImages from '../../../../hooks/usePastImages';
import { useCustomer } from '../../../../contexts/CustomerContext';
import routes from '../../../../routes';
import YearbookHomeForm from './YearbookHomeForm';
import { fetchYearbookHomeDelivery } from '../../../../api/yearbook_home_delivery';
import orderYearbookShipToHome from '../../../../assets/OrderYearbook_ShiptoHome_Personalized.jpg';
import { trackFunnel, FUNNEL_EVENTS } from '../../../../utils/trackFunnel';
import { yearbookViewEvent } from './yearbookViewEvent';

const ERROR = 'ERROR'
const DONE = 'DONE'
const FETCHING = 'FETCHING'
const TOO_MANY_RESULTS = 'TOO_MANY_RESULTS'

export const useStudent = (params) => {
  const [student, setStudent] = useState()
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    setStatus(FETCHING)

    Student
      .includes('school')
      .where({ ...params })
      .extraParams({ yba: true })
      .all()
      .then((res) => {
        if (res.data.length === 1) {
          setStudent(res.data[0])
          setStatus(DONE)
        } else {
          setStudent(res.data[res.data.length - 1])
          setStatus(DONE)
        }
      })
      .catch(() => {
        setStatus(ERROR)
      })
  }, [params.studentId, params.schoolId])

  return { student, status }
}

export const useYearbookPackage = (schoolId, campaign) => {
  const [yearbook, setYearbook] = useState();
  const [yearbooks, setYearbooks] = useState([])
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    if (!schoolId) return

    const priceScope = Price.where({ for_school: schoolId })

    // schoolId is a plain attribute on Price, so YearbookForm can tell a
    // school-specific price from a generic one without sideloading
    // options.prices.school. (The Podesta Ranch "zero packages" incident was
    // blamed on that sideload; it was the server caching this query with its
    // clock frozen -- removed from Api::PackagesController, see
    // spec/requests/api/packages_cache_freshness_spec.rb.)
    Package
      .includes({ options: 'prices' })
      .merge({ prices: priceScope })
      .where({ schoolId })
      .where({ has_prices_for_school: schoolId })
      .where({ name: { match: 'yearbook' } })
      .all()
      .then((res) => {
        setYearbooks(res.data)
        setYearbook(res.data[0])
        setStatus(DONE)
      })
      .catch(() => setStatus(ERROR))
  }, [schoolId])

  return { yearbook, yearbooks, status }
}

// Once the in-window package lookup comes up empty, ask the backend whether
// the school is in ship-to-home mode. `enabled` gates the call so it only fires
// for expired schools, not every visit.
export const useYearbookHomeDelivery = (schoolId, enabled) => {
  const [homeDelivery, setHomeDelivery] = useState()
  const [status, setStatus] = useState(FETCHING)

  useEffect(() => {
    if (!enabled || !schoolId) return

    setStatus(FETCHING)

    fetchYearbookHomeDelivery(schoolId)
      .then((res) => {
        setHomeDelivery(res.data)
        setStatus(DONE)
      })
      .catch(() => setStatus(ERROR))
  }, [schoolId, enabled])

  return { homeDelivery, status }
}

const Yearbooks = () => {
  const { sch: schoolId, sid: studentId, campaign, message_id: messageId, from } = useQueryString()
  const { student, status } = useStudent({ schoolId, studentId })
  const { yearbook, yearbooks, status: packageStatus } = useYearbookPackage(schoolId, campaign)
  const needHome = Boolean(student) && packageStatus === DONE && !yearbook
  const { homeDelivery, status: homeStatus } = useYearbookHomeDelivery(schoolId, needHome)
  const [_, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const { customer, setCustomer } = useCustomer();

  useEffect(() => {
    if (!student?.email) return;

    setCustomer({ ...customer, email: student.email });
  }, [student]);

  const { data: pastImages } = usePastImages({ studentId: student?.id });

  // Funnel log (docs/purchase_flow.md "Funnel events"): which of the page's
  // three outcomes the parent saw, and at what price, once it has settled.
  // trackFunnel sends it once per student per page session.
  const yearbookView = yearbookViewEvent({
    student,
    yearbook,
    packageDone: packageStatus === DONE,
    homeFetching: needHome && homeStatus === FETCHING,
    homeDelivery,
  });
  const yearbookViewState = yearbookView && yearbookView.state;
  useEffect(() => {
    if (!yearbookView) return;
    trackFunnel(FUNNEL_EVENTS.YEARBOOK_VIEWED, {
      studentId: student.id,
      schoolId: student.school?.id || schoolId,
      packageId: yearbookView.packageId,
      cartId: customer?.cart?.id,
      messageId,
      properties: {
        surface: 'yearbook_landing',
        state: yearbookView.state,
        price: yearbookView.price,
        home_delivery_state: yearbookView.homeDeliveryState,
        campaign: campaign || yearbook?.campaign,
        from,
      },
    });
  }, [yearbookViewState, student?.id]);

  setIsUsingAccessCode(true)

  if (status === FETCHING) return <Fetching />
  if (status === TOO_MANY_RESULTS || status === ERROR) {
    return <ErrorState message="We were unable to find your student. Please check back again later." />
  }

  // In-window: the normal ship-to-school flow.
  if (student && yearbook) {
    return (
      <Container maxWidth="md">
        <StudentPromoImage campaign={campaign || yearbook.campaign} studentId={student.id} />
        <YearbookForm
          student={student}
          school={student.school}
          defaultYearbook={yearbook}
          yearbooks={yearbooks}
          messageId={messageId}
          from={from}
        />
      </Container>
    )
  }

  // Student loaded but no current price window — wait for the home-delivery
  // lookup, then show the ship-to-home variant if the program is open.
  if (student && packageStatus !== DONE) return <Fetching />
  if (needHome && homeStatus === FETCHING) return <Fetching />

  if (homeDelivery && homeDelivery.state === 'home') {
    return (
      <Container maxWidth="md">
        <img
          src={orderYearbookShipToHome}
          alt="Don't miss out on this year's memories — personalize your cover! Yearbooks ship to your home, not the school."
          style={{ width: '100%', height: 'auto', display: 'block', marginBottom: '1.5rem' }}
        />
        <YearbookHomeForm
          student={student}
          school={student.school}
          homeDelivery={homeDelivery}
          messageId={messageId}
          from={from}
        />
      </Container>
    )
  }

  return (
    <Typography align="center" fontStyle="italic">
      The deadline to order yearbooks for this year has passed.  Yearbooks are no longer available.
    </Typography>
  )
}

export default Yearbooks
